// ─────────────────────────────────────────────────────────────────────────────
// Settings page — platform configuration. This component only wires things
// together:
//     • local state — the active tab + the header's save status
//     • saveRef     — CompanyProfile registers its save fn here so the header's
//                     Save Changes button can invoke it (it's the only tab with
//                     a real save endpoint)
//   All rendering lives in ./components; all fetching in ./hooks.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef, useCallback, useMemo } from "react";
import styles from "./Settings.module.css";
import { useAuthState } from "../../features/auth/model/AuthProvider";
import { usePermissions, MODULES } from "../../features/auth/model/usePermissions";
import SettingsHeader from "./components/SettingsHeader";
import SettingsRail, { TABS } from "./components/SettingsRail";
import AccessDenied from "./components/AccessDenied";
import CompanyProfile from "./components/CompanyProfile";
import AdminRoles from "./components/AdminRoles";
import Security from "./components/Security";
import Integrations from "./components/Integrations";
import Notifications from "./components/Notifications";

// Tabs backed by a PUT endpoint (their pane registers a save fn on saveRef).
const SAVEABLE_TABS = new Set(["company", "notifications"]);

export default function Settings() {
  // Settings visibility and edit rights come from the permission matrix:
  // None → Access Denied, Read → read-only (IT Manager), Full → editable (Admin).
  const { user } = useAuthState();
  const { canAccess, canWrite } = usePermissions();
  const canView = canAccess(MODULES.SETTINGS);
  const canEdit = canWrite(MODULES.SETTINGS);

  // The Security tab lists company users and flips their MFA, so it answers to
  // user_management — not settings. A Security Admin who can't see the directory
  // shouldn't be shown the tab at all.
  const canSeeUsers   = canAccess(MODULES.USER_MANAGEMENT);
  const canEditUsers  = canWrite(MODULES.USER_MANAGEMENT);
  const visibleTabs   = useMemo(
    () => TABS.filter((t) => t.key !== "security" || canSeeUsers),
    [canSeeUsers],
  );

  const [tab, setTab] = useState("company");

  // Stable ref — CompanyProfile registers its save fn here.
  const saveRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const handleSave = useCallback(async () => {
    if (!saveRef.current) return;
    setSaving(true); setSaved(false);
    try {
      await saveRef.current();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      // The pane surfaces the error banner itself.
    } finally {
      setSaving(false);
    }
  }, []);

  if (!canView) {
    return (
      <div className={styles.page}>
        <div className={styles.panel}>
          <SettingsHeader/>
          <AccessDenied role={user?.role}/>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        <SettingsHeader
          onSave={handleSave}
          saving={saving}
          saved={saved}
          canSave={SAVEABLE_TABS.has(tab) && canEdit}
        />

        <div className={styles.body}>
          <SettingsRail tab={tab} onTab={setTab} tabs={visibleTabs}/>

          <div className={styles.content}>
            {tab === "company"       && <CompanyProfile saveRef={saveRef} readOnly={!canEdit}/>}
            {tab === "roles"         && <AdminRoles/>}
            {tab === "security"      && canSeeUsers && <Security readOnly={!canEditUsers}/>}
            {tab === "integrations"  && <Integrations readOnly={!canEdit}/>}
            {tab === "notifications" && <Notifications saveRef={saveRef} readOnly={!canEdit}/>}
          </div>
        </div>
      </div>
    </div>
  );
}
